import { useState, type FormEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store/auth'
import { loadProfile } from '@/hooks/useAuth'
import { AuthLayout } from '@/components/gg/AuthLayout'

export function Signup() {
  const navigate = useNavigate()
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [clientSlug, setClientSlug] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [checkEmail, setCheckEmail] = useState(false)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    // client_slug is read by the handle_new_user() trigger, which links the new
    // account to its client via user_clients. Without it the account exists but
    // auth_client_id() stays null and every query returns nothing.
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName.trim() || null,
          client_slug: clientSlug.trim().toLowerCase(),
          // Set by a gogrov.com/r/<code> link. Redeemed by the signup trigger;
          // a signup without a code is simply not a referral.
          referral_code: readReferral(),
        },
      },
    })

    if (error) {
      setError(error.message)
      setLoading(false)
      return
    }

    // No session means the project requires email confirmation.
    if (!data.session) {
      setCheckEmail(true)
      setLoading(false)
      return
    }

    const { setSession, setLoading: setAuthLoading } = useAuthStore.getState()
    setSession(data.session)
    await loadProfile(data.session.user.id)
    setAuthLoading(false)
    navigate('/app', { replace: true })
  }

  if (checkEmail) {
    return (
      <AuthLayout
        eyebrow="Almost there"
        title="Check your email"
        lede={<>We sent a confirmation link to <strong>{email}</strong>. Open it to finish setting up your account, then sign in.</>}
      >
        <Link to="/login" className="gg-btn gg-btn--amber gg-btn--block auth2__submit">Back to sign in</Link>
      </AuthLayout>
    )
  }

  const referral = readReferral()

  return (
    <AuthLayout
      eyebrow="Get started"
      title="Create your account"
      lede="Your local stories, scripted and ready to record."
      foot={<>By creating an account you agree to our <Link to="/terms">terms</Link> and <Link to="/privacy">privacy policy</Link>.</>}
    >
      <form onSubmit={(e) => { void handleSubmit(e) }}>
        <label className="gg-field">
          <span>Your name</span>
          <input className="gg-input" id="fullName" type="text" value={fullName}
            onChange={(e) => setFullName(e.target.value)} autoComplete="name" />
        </label>
        <label className="gg-field">
          <span>Email</span>
          <input className="gg-input" id="email" type="email" value={email}
            onChange={(e) => setEmail(e.target.value)} required autoComplete="email" inputMode="email" />
        </label>
        <label className="gg-field">
          <span>Password</span>
          <input className="gg-input" id="password" type="password" value={password}
            onChange={(e) => setPassword(e.target.value)} required minLength={8} autoComplete="new-password" />
          <span className="gg-help auth2__hint">At least 8 characters.</span>
        </label>
        <label className="gg-field">
          <span>Invite code</span>
          <input className="gg-input" id="clientSlug" type="text" value={clientSlug}
            onChange={(e) => setClientSlug(e.target.value)} required autoComplete="off" autoCapitalize="none"
            spellCheck={false} aria-describedby="clientSlug-hint" />
          <span id="clientSlug-hint" className="gg-help auth2__hint">
            The code from your GoGrov welcome email. It links your account to your brief.
          </span>
        </label>
        {referral && <p className="gg-note">Referred with the code <strong>{referral}</strong>.</p>}
        {error && <p className="gg-error" role="alert">{error}</p>}
        <button type="submit" disabled={loading} className="gg-btn gg-btn--amber gg-btn--block auth2__submit">
          {loading ? 'Creating your account' : 'Create account'}
        </button>
      </form>
      <div className="auth2__links">
        <Link to="/login" className="auth2__link">Already have an account? <b>Sign in</b></Link>
      </div>
    </AuthLayout>
  )
}

function readReferral(): string | null {
  try {
    const raw = localStorage.getItem('gg_referral')
    if (!raw) return null
    const { code, at } = JSON.parse(raw) as { code: string; at: number }
    // A referral link counts for 30 days.
    if (Date.now() - at > 30 * 86400000) return null
    return code
  } catch { return null }
}
